import type { CanvasTexture } from 'three';
import { Rng } from '../../core/rng';
import { canvasTexture } from '../../render/materials/canvasTexture';

/** One facade module = one window bay (3 m) × one storey (3.2 m), drawn at 128×136 px and tiled. */
export const MODULE = { width: 3, height: 3.2, px: 128, py: 136 } as const;

function grime(ctx: CanvasRenderingContext2D, strength: number): void {
  const g = ctx.createLinearGradient(0, 0, 0, MODULE.py);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(1, `rgba(40,30,20,${strength})`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, MODULE.px, MODULE.py);
}

/** Airport City / Ridge glass curtain wall. */
export function paintGlass(tint: string, seed: number): CanvasTexture {
  const rng = new Rng(seed);
  return canvasTexture(MODULE.px, MODULE.py, (ctx) => {
    const sky = ctx.createLinearGradient(0, 0, MODULE.px, MODULE.py);
    sky.addColorStop(0, '#dcebf5');
    sky.addColorStop(0.35, tint);
    sky.addColorStop(1, tint);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, MODULE.px, MODULE.py);
    for (let x = 0; x < MODULE.px; x += 32) {
      ctx.fillStyle = `rgba(255,255,255,${rng.range(0, 0.12)})`;
      ctx.fillRect(x, 0, 32, MODULE.py);
    }
    ctx.fillStyle = 'rgba(20,30,40,0.55)';
    for (let x = 0; x <= MODULE.px; x += 32) ctx.fillRect(x - 1, 0, 3, MODULE.py);
    ctx.fillRect(0, MODULE.py - 16, MODULE.px, 16);
  });
}

/** White/cream concrete office with strip windows, like the older Ridge blocks. */
export function paintConcreteOffice(wall: string, seed: number): CanvasTexture {
  const rng = new Rng(seed);
  return canvasTexture(MODULE.px, MODULE.py, (ctx) => {
    ctx.fillStyle = wall;
    ctx.fillRect(0, 0, MODULE.px, MODULE.py);
    const glass = ctx.createLinearGradient(0, 34, 0, 96);
    glass.addColorStop(0, '#5d7a8c');
    glass.addColorStop(1, '#1f2d38');
    ctx.fillStyle = glass;
    ctx.fillRect(0, 34, MODULE.px, 62);
    ctx.fillStyle = wall;
    for (let x = 0; x < MODULE.px; x += 64) ctx.fillRect(x, 30, 8, 70);
    if (rng.next() > 0.6) {
      ctx.fillStyle = '#e8e8e8';
      ctx.fillRect(84, 100, 26, 16);
    }
    grime(ctx, 0.12);
  });
}

/** Painted two-to-four storey shophouse upper floor: louvred window, burglar bars, balcony. */
export function paintShophouse(wall: string, seed: number): CanvasTexture {
  const rng = new Rng(seed);
  return canvasTexture(MODULE.px, MODULE.py, (ctx) => {
    ctx.fillStyle = wall;
    ctx.fillRect(0, 0, MODULE.px, MODULE.py);
    ctx.fillStyle = '#2e2a26';
    ctx.fillRect(30, 26, 68, 66);
    ctx.fillStyle = rng.pick(['#7a5c3e', '#4a6d7c', '#8a8a8a', '#355e3b']);
    for (let y = 30; y < 90; y += 8) ctx.fillRect(34, y, 60, 4);
    ctx.strokeStyle = '#1b1b1b';
    ctx.lineWidth = 2;
    for (let x = 36; x < 98; x += 10) {
      ctx.beginPath();
      ctx.moveTo(x, 26);
      ctx.lineTo(x, 92);
      ctx.stroke();
    }
    if (rng.next() > 0.5) {
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.fillRect(12, 100, 104, 6);
      ctx.strokeStyle = '#333';
      for (let x = 14; x < 116; x += 8) {
        ctx.beginPath();
        ctx.moveTo(x, 104);
        ctx.lineTo(x, 124);
        ctx.stroke();
      }
      ctx.fillRect(12, 122, 104, 4);
    }
    if (rng.next() > 0.7) {
      ctx.fillStyle = '#dcdcdc';
      ctx.fillRect(100, 60, 22, 16);
    }
    grime(ctx, 0.3);
  });
}

/** Ground-floor shop: open front with goods or a roller shutter, sign board on top. */
export function paintShopFront(sign: { text: string; bg: string; fg: string }, seed: number): CanvasTexture {
  const rng = new Rng(seed);
  return canvasTexture(512, 144, (ctx) => {
    ctx.fillStyle = '#d9cbb3';
    ctx.fillRect(0, 0, 512, 144);
    ctx.fillStyle = sign.bg;
    ctx.fillRect(0, 0, 512, 40);
    ctx.fillStyle = sign.fg;
    ctx.font = 'bold 30px Impact, "Arial Black", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(sign.text, 256, 22, 490);
    for (let i = 0; i < 4; i += 1) {
      const x = 8 + i * 126;
      if (rng.next() > 0.4) {
        ctx.fillStyle = '#231d18';
        ctx.fillRect(x, 48, 118, 96);
        const goods = ['#e63946', '#f4a261', '#2a9d8f', '#ffd166', '#118ab2', '#ffffff'];
        for (let g = 0; g < 14; g += 1) {
          ctx.fillStyle = rng.pick(goods);
          ctx.fillRect(x + 6 + rng.range(0, 96), 60 + rng.range(0, 70), rng.range(6, 16), rng.range(6, 18));
        }
      } else {
        ctx.fillStyle = '#9aa0a6';
        ctx.fillRect(x, 48, 118, 96);
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        for (let y = 52; y < 144; y += 6) ctx.fillRect(x, y, 118, 2);
      }
    }
  });
}

/** Cantonments compound wall with pillars and bougainvillea spilling over. */
export function paintCompoundWall(wall: string, seed: number): CanvasTexture {
  const rng = new Rng(seed);
  return canvasTexture(512, 96, (ctx) => {
    ctx.fillStyle = wall;
    ctx.fillRect(0, 0, 512, 96);
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    for (let x = 0; x < 512; x += 128) ctx.fillRect(x, 0, 14, 96);
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.fillRect(0, 0, 512, 8);
    if (rng.next() > 0.4) {
      ctx.fillStyle = '#3d3d3d';
      ctx.fillRect(200, 22, 110, 74);
      ctx.fillStyle = '#1f1f1f';
      for (let x = 206; x < 310; x += 10) ctx.fillRect(x, 26, 3, 70);
    }
    for (let i = 0; i < 26; i += 1) {
      ctx.fillStyle = rng.pick(['#d6246e', '#e84393', '#b5179e', '#2d6a4f', '#40916c']);
      ctx.beginPath();
      ctx.arc(rng.range(0, 512), rng.range(0, 26), rng.range(6, 16), 0, Math.PI * 2);
      ctx.fill();
    }
    grime(ctx, 0.2);
  });
}
